/**
 * Pure utility functions for message formatting and display
 * These functions are already tested in src__tests__/Conversation.test.ts
 */

import {
  mailUnreadOutline,
  mailOpenOutline,
  mailOutline,
  cardOutline,
  receiptOutline,
  documentTextOutline,
  ticketOutline,
  chatbubblesOutline,
  chatbubbleOutline,
  gitMergeOutline,
  callOutline,
  personOutline,
  personAddOutline,
  peopleOutline,
  alertCircleOutline,
  document as documentIcon,
} from 'ionicons/icons';

/**
 * Extract initials from a name for avatar display
 * @example "John Doe" -> "JD"
 * @example "John Michael Doe" -> "JMD"
 */
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

/**
 * Map Wix notification icon identifiers to Ionic icon components
 * Handles 30+ icon types for different notification categories:
 * - Email (clicked, opened, received)
 * - Invoices (paid, sent)
 * - Quotes and pricing
 * - Bookings
 * - Shoutouts/broadcasts
 * - Chat messages
 * - Contact merges
 * - Virtual calls
 * - Site member activities
 */
export const getIconForIdentifier = (iconIdentifier: string): string => {
  const icon = iconIdentifier.toLowerCase();

  // Shoutout email notifications (most specific first)
  if (icon === 'shoutout.email.click.icon') return mailUnreadOutline;
  if (icon === 'shoutout.email.open.icon') return mailOpenOutline;
  if (icon === 'shoutout.email.sent.icon' || icon === 'shoutout.email.received.icon')
    return mailOutline;

  // General email notifications
  if (icon === 'email.clicked') return mailUnreadOutline;
  if (icon === 'email.open') return mailOpenOutline;
  if (icon.startsWith('email.') || icon === 'email' || icon === 'mail') return mailOutline;

  // Mailing list subscriptions
  if (icon.includes('subscribe')) return mailOutline;

  // Invoice notifications - PAID uses card icon, SENT uses receipt icon
  if (icon === 'invoice.paid' || icon === 'invoice.paid.icon') return cardOutline;
  if (icon === 'invoice.sent' || icon === 'invoice.send' || icon === 'invoice.send.icon')
    return receiptOutline;
  if (icon.startsWith('invoice.')) return receiptOutline;

  // Quote notifications - use document icon
  if (icon === 'quote.sent' || icon === 'quote.accepted' || icon === 'quote') return documentIcon;
  if (
    icon === 'price.quote.send.icon' ||
    icon === 'price.quote.accept.icon' ||
    icon.startsWith('price.quote.')
  )
    return documentIcon;

  // Form submissions
  if (icon.startsWith('form.') || icon === 'form' || icon === 'submit')
    return documentTextOutline;

  // Booking notifications
  if (icon.startsWith('booking.') || icon === 'booking') return ticketOutline;

  // Shoutout/broadcast
  if (icon.startsWith('shoutout.') || icon === 'shoutout') return chatbubblesOutline;

  // Chat messages
  if (icon.startsWith('chat.') || icon === 'message') return chatbubbleOutline;

  // Contact merge
  if (icon === 'merge' || icon.includes('merged')) return gitMergeOutline;

  // Virtual number calls
  if (icon === 'virtual-number.incoming-call.icon') return callOutline;
  if (icon === 'virtual-number.outgoing-call.icon') return callOutline;

  // General phone/call
  if (icon.startsWith('call.') || icon === 'call' || icon === 'phone') return callOutline;

  // Site member notifications - exact Wix icon identifier
  if (icon === 'site.member.joined.icon') return personOutline;

  // Site member - REQUESTED to join (pending approval)
  if (icon === 'member.requested' || icon === 'member.request' || icon.includes('request'))
    return personAddOutline;

  // Site member - BECAME member (approved/registered)
  if (icon.startsWith('member.') || icon === 'member' || icon === 'user') return personOutline;

  // People/contacts
  if (icon.startsWith('contact.') || icon === 'people') return peopleOutline;

  // Default - unrecognized icon
  console.log(`⚠️ Unrecognized icon: "${iconIdentifier}"`);
  return alertCircleOutline;
};

/**
 * Format business notification text for better display
 * Shortens verbose email campaign notifications
 * @example "John clicked your email campaign" -> "Email clicked"
 */
export const formatBusinessNotificationText = (text: string): string => {
  const lowerText = text.toLowerCase();

  // Email campaign notifications - shorten these
  if (lowerText.includes('clicked your email campaign')) return 'Email clicked';
  if (lowerText.includes('opened your email campaign')) return 'Email opened';
  if (lowerText.includes('received your email campaign')) return 'Email received';

  // All other notifications - keep original text
  return text;
};

/**
 * Format date for display in message separator
 * @example new Date() -> "Today"
 * @example yesterday -> "Yesterday"
 * @example older -> "Monday, January 19, 2024"
 */
export const formatDateSeparator = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time to midnight for comparison
  const msgDate = new Date(date);
  msgDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);

  if (msgDate.getTime() === today.getTime()) {
    return 'Today';
  } else if (msgDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
};

/**
 * Format time for message timestamps
 * @example new Date() -> "2:35 PM"
 */
export const formatTimestamp = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Check if date separator should be shown between two messages
 */
export const shouldShowDateSeparator = (
  currentMsg: { _createdDate: Date },
  previousMsg: { _createdDate: Date } | undefined
): boolean => {
  if (!previousMsg) return true;

  const currentDate = new Date(currentMsg._createdDate);
  const previousDate = new Date(previousMsg._createdDate);

  currentDate.setHours(0, 0, 0, 0);
  previousDate.setHours(0, 0, 0, 0);

  return currentDate.getTime() !== previousDate.getTime();
};
